var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { useState, useEffect, useCallback, useMemo, createRef, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { textInputHeights } from '../TextInput/constants';
import { TextInput, } from '../TextInput/index';
import { sizes } from './constants';
const typeaheadContainerStyles = css `
  position: relative;
`;
const additionalListStyles = css `
    border-bottom: ${styleUtils.rem(1)} ${theme.colors.gray400};
    border-left: ${styleUtils.rem(1)} ${theme.colors.gray400};
    border-right: ${styleUtils.rem(1)} ${theme.colors.gray400};
    border-top: ${styleUtils.rem(1)} solid ${theme.colors.gray400};
    box-shadow: -${styleUtils.rem(3)} 0 ${styleUtils.rem(3)} -${styleUtils.rem(3)} rgb(0, 0, 0, 0.8),
      0 ${styleUtils.rem(3)} ${styleUtils.rem(3)} -${styleUtils.rem(3)} rgb(0, 0, 0, 0.8),
      ${styleUtils.rem(3)} 0 ${styleUtils.rem(3)} -${styleUtils.rem(3)} rgb(0, 0, 0, 0.8);
    background-color: ${theme.colors.gray100};
    ${theme.typeStyles.bodyMediumStyles}
    list-style: none;
    margin: 0;
    max-height: ${styleUtils.rem(200)};
    overflow: auto;
    padding: 0;

    & > li {
      padding-bottom: ${theme.spacing.x2};
      padding-left: ${theme.spacing.x3};
      padding-right: ${theme.spacing.x3};
      padding-top: ${theme.spacing.x2};

      &:hover {
        background-color: ${theme.colors.gray300};
        cursor: pointer;
      }
    }
  `;
const defaultProps = {
    // ...TextInput.defaultProps
    items: [],
    onChange: () => { },
    onSelect: () => { },
    size: sizes.medium,
    value: '',
};
const Typeahead = (_a) => {
    var { items = defaultProps.items, onChange = defaultProps.onChange, onSelect = defaultProps.onSelect, partialMatch, size = defaultProps.size, value = defaultProps.value } = _a, textInputProps = __rest(_a, ["items", "onChange", "onSelect", "partialMatch", "size", "value"]);
    const [listItems, setListItems,] = useState(Array.prototype);
    const list = createRef();
    const [highlightedIndex, setHighlightedIndex,] = useState(-1);
    const [isDropdownVisible, setIsDropdownVisible,] = useState(false);
    const hideList = useCallback(() => {
        setHighlightedIndex(-1);
        setIsDropdownVisible(false);
    }, []);
    const scrollTo = useCallback((index) => {
        if (list.current) {
            const items = list.current.querySelectorAll('li');
            if (items[index]) {
                const listPosition = list.current.getBoundingClientRect();
                const itemPosition = items[index].getBoundingClientRect();
                const itemHeight = items[index].offsetHeight;
                if (itemPosition.bottom - itemHeight < listPosition.top) {
                    list.current.scrollTop -= itemHeight;
                }
                else if (itemPosition.top + itemHeight > listPosition.bottom) {
                    list.current.scrollTop += itemHeight;
                }
            }
        }
    }, [
        list,
    ]);
    const filterItems = useCallback((items, value) => {
        if (!value.trim()) {
            return items;
        }
        const lowerValue = value.toLowerCase();
        if (partialMatch) {
            return items.filter((item) => item.toLowerCase().indexOf(lowerValue) > -1);
        }
        return items.filter((item) => item.toLowerCase().indexOf(lowerValue) === 0);
    }, [
        partialMatch,
    ]);
    const handleFocus = useCallback(() => {
        setIsDropdownVisible(true);
    }, []);
    const handleBlur = useCallback(() => {
        window.setTimeout(() => hideList(), 200);
    }, [
        hideList,
    ]);
    const handleInputChange = useCallback((event) => {
        onChange(event);
        setHighlightedIndex(0);
        setIsDropdownVisible(true);
    }, [
        onChange,
    ]);
    // Prevent form submissions when user is selecting an item with 'Enter'
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter' && highlightedIndex !== -1) {
            event.preventDefault();
        }
    }, [
        highlightedIndex,
    ]);
    const handleItemSelect = useCallback((item) => {
        hideList();
        onSelect(item);
    }, [
        hideList,
        onSelect,
    ]);
    const handleKeyUp = useCallback((event) => {
        const filteredListItems = filterItems(items, value);
        let newIndex;
        switch (event.key) {
            case 'Escape':
                hideList();
                break;
            case 'ArrowDown':
                newIndex = highlightedIndex + 1;
                setHighlightedIndex(Math.min(newIndex, filteredListItems.length - 1));
                setIsDropdownVisible(true);
                scrollTo(newIndex);
                break;
            case 'ArrowUp':
                newIndex = highlightedIndex - 1;
                setHighlightedIndex(Math.max(newIndex, 0));
                setIsDropdownVisible(true);
                scrollTo(newIndex);
                break;
            case 'Enter':
                if (filteredListItems[highlightedIndex]) {
                    handleItemSelect(filteredListItems[highlightedIndex]);
                }
                break;
            default:
                break;
        }
    }, [
        filterItems,
        handleItemSelect,
        hideList,
        highlightedIndex,
        items,
        scrollTo,
        value,
    ]);
    const listItemOutput = useMemo(() => (listItems.map((item, index) => (_jsx("li", Object.assign({ css: index === highlightedIndex && css `
            background-color: ${theme.colors.gray300};
          `, onClick: (event) => {
            event.stopPropagation();
            handleItemSelect(item);
        } }, { children: item }), `typeahead-key-${item}-${index}`)))), [
        highlightedIndex,
        handleItemSelect,
        listItems,
    ]);
    const additionalListContainerStyles = useMemo(() => (css `
        position: absolute;
        top: ${styleUtils.rem(textInputHeights[size])};
        width: 100%;
        z-index: 999999;
      `), [
        size,
    ]);
    useEffect(() => {
        setListItems(filterItems(items, value));
    }, [
        items,
        value,
        filterItems,
    ]);
    return (_jsxs("div", Object.assign({ css: typeaheadContainerStyles }, { children: [_jsx(TextInput, Object.assign({}, textInputProps, { onBlur: handleBlur, onChange: handleInputChange, onFocus: handleFocus, onKeyDown: handleKeyDown, onKeyUp: handleKeyUp, value: value })), _jsx("div", Object.assign({ css: additionalListContainerStyles }, { children: isDropdownVisible
                    && listItems.length > 0
                    && (_jsx("ul", Object.assign({ css: additionalListStyles, ref: list }, { children: listItemOutput }))) }))] })));
};
export { Typeahead };
